import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import type { Renderable, Toast, ToastOptions } from 'react-hot-toast';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import Button from '../general/button';
export class ToastUtil {
  static async promise<T>(
    promiseFn: Promise<T>,
    msgs: {
      loading: string;
      success: string;
      error: string;
    } | null = null,
    displayError = true
  ) {
    if (msgs === null)
      msgs = {
        loading: 'Saving...',
        success: 'Saved successfully',
        error: 'There was an issue saving'
      };
    let id = uuidv4();
    try {
      id = toast.custom(
        (t) => (
          <ToastDisplay
            {...t}
            variant="loading"
            title={msgs!.loading || 'Loading'}
            message={null}
            canDismiss={false}
          />
        ),
        { id }
      );
      const result = await promiseFn;
      toast.custom(
        (t) => <ToastDisplay {...t} variant="success" title="Success" message={msgs!.success} />,
        { id }
      );
      return result;
    } catch (err: any) {
      console.error(err);
      toast.custom(
        (t) => (
          <ToastDisplay
            {...t}
            variant="error"
            title="Failed"
            message={msgs!.error}
            error={displayError ? err : ''}
          />
        ),
        { id }
      );
      throw err;
    }
  }

  static setMessage(
    variant: Variant,
    title: Renderable,
    description: Renderable,
    actions?: ToastAction[],
    icon?: Renderable,
    options?: ToastOptions
  ) {
    return toast.custom(
      (t) => (
        <ToastDisplay
          {...t}
          variant={variant}
          message={description}
          title={title}
          actions={actions}
          icon={icon}
        />
      ),
      options
    );
  }

  static remove(id: string) {
    toast.remove(id);
  }
  static information(title: Renderable = 'Information', message: Renderable) {
    return this.setMessage('information', title, message);
  }

  static loading(message: Renderable) {
    return this.setMessage('loading', '', message);
  }

  static success(message: Renderable) {
    return this.setMessage('success', 'Success', message);
  }

  static error(message: Renderable, options?: ToastOptions) {
    return this.setMessage('error', 'Failed', message, undefined, undefined, options);
  }

  static warning(title: Renderable, message: Renderable, options?: ToastOptions) {
    return this.setMessage('warning', title, message, undefined, undefined, options);
  }
}
type Variant = 'loading' | 'error' | 'success' | 'information' | 'warning';

interface ToastProps extends Toast {
  canDismiss?: boolean;
  error?: any;
  title: Renderable;
  message: Renderable;
  variant: Variant;
  actions?: ToastAction[];
  icon?: Renderable;
}

interface ToastAction {
  label: string;
  onClick: () => void;
}

const bgMap = {
  success: 'bg-green-500',
  error: 'bg-red-500',
  warning: 'bg-amber-500',
  loading: 'bg-sky-500',
  information: 'bg-sky-500'
} as const;

const closeMap = {
  success: 'text-green-500 hover:text-green-600 ',
  error: 'text-red-500 hover:text-red-600 ',
  warning: 'text-amber-500 hover:text-amber-600',
  loading: 'text-sky-500 hover:text-sky-500',
  information: 'text-sky-500 hover:text-sky-500'
} as const;

const iconMap = {
  success: (
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M32 9C19.2975 9 9 19.2975 9 32C9 44.7026 19.2975 55 32 55C44.7026 55 55 44.7026 55 32C55 19.2975 44.7026 9 32 9ZM12.0474 32C12.0474 20.9805 20.9805 12.0474 32 12.0474C43.0195 12.0474 51.9526 20.9805 51.9526 32C51.9526 43.0195 43.0195 51.9526 32 51.9526C20.9805 51.9526 12.0474 43.0195 12.0474 32ZM42.4059 26.7311C43.0255 26.0881 43.0122 25.059 42.3764 24.4325C41.7405 23.8061 40.7229 23.8194 40.1033 24.4624L28.9146 36.0742L23.896 30.8657C23.2764 30.2227 22.2587 30.2094 21.6229 30.8358C20.987 31.4623 20.9738 32.4914 21.5934 33.1344L27.7633 39.5377C28.0659 39.8517 28.4811 40.0288 28.9146 40.0288C29.3482 40.0288 29.7634 39.8517 30.0659 39.5377L42.4059 26.7311Z"
        fill="#4CAF50"
      />
    </svg>
  ),
  error: (
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9 32C9 19.2975 19.2975 9 32 9C44.7026 9 55 19.2975 55 32C55 44.7026 44.7026 55 32 55C19.2975 55 9 44.7026 9 32ZM32 12.0474C20.9805 12.0474 12.0474 20.9805 12.0474 32C12.0474 43.0195 20.9805 51.9526 32 51.9526C43.0195 51.9526 51.9526 43.0195 51.9526 32C51.9526 20.9805 43.0195 12.0474 32 12.0474ZM26.4009 24.0212C25.7436 23.3639 24.6779 23.3639 24.0206 24.0212C23.3633 24.6785 23.3633 25.7442 24.0206 26.4015L29.6192 32L24.0206 37.5986C23.3633 38.2558 23.3633 39.3215 24.0206 39.9788C24.6779 40.6361 25.7436 40.6361 26.4009 39.9788L31.9994 34.3803L37.598 39.9788C38.2553 40.6361 39.3209 40.6361 39.9782 39.9788C40.6355 39.3215 40.6355 38.2558 39.9782 37.5986L34.3797 32L39.9782 26.4015C40.6355 25.7442 40.6355 24.6785 39.9782 24.0212C39.3209 23.3639 38.2553 23.3639 37.598 24.0212L31.9994 29.6198L26.4009 24.0212Z"
        fill="#F44336"
      />
    </svg>
  ),
  warning: (
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M32 9C19.2975 9 9 19.2975 9 32C9 44.7026 19.2975 55 32 55C44.7026 55 55 44.7026 55 32C55 19.2975 44.7026 9 32 9ZM12.0474 32C12.0474 20.9805 20.9805 12.0474 32 12.0474C43.0195 12.0474 51.9526 20.9805 51.9526 32C51.9526 43.0195 43.0195 51.9526 32 51.9526C20.9805 51.9526 12.0474 43.0195 12.0474 32ZM30.1105 20.8027C30.1105 19.7054 30.9278 18.7762 31.9831 18.7762C33.0384 18.7762 33.8557 19.7054 33.8557 20.8027V35.2592C33.8557 36.3565 33.0384 37.2857 31.9831 37.2857C30.9278 37.2857 30.1105 36.3565 30.1105 35.2592V20.8027ZM30.1105 43.3587C30.1105 42.3307 30.9468 41.4936 31.9831 41.4936H32.0161C33.0524 41.4936 33.8887 42.3307 33.8887 43.3587C33.8887 44.3867 33.0524 45.2239 32.0161 45.2239H31.9831C30.9468 45.2239 30.1105 44.3867 30.1105 43.3587Z"
        fill="#FF9800"
      />
    </svg>
  ),
  information: (
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M32 9C19.2975 9 9 19.2975 9 32C9 44.7026 19.2975 55 32 55C44.7026 55 55 44.7026 55 32C55 19.2975 44.7026 9 32 9ZM12.0474 32C12.0474 20.9805 20.9805 12.0474 32 12.0474C43.0195 12.0474 51.9526 20.9805 51.9526 32C51.9526 43.0195 43.0195 51.9526 32 51.9526C20.9805 51.9526 12.0474 43.0195 12.0474 32ZM26.3327 28.2215C26.3327 27.1782 27.1785 26.3324 28.2219 26.3324H32.0001C33.0434 26.3324 33.8892 27.1782 33.8892 28.2215V41.4453H35.7783C36.8217 41.4453 37.6674 42.2911 37.6674 43.3344C37.6674 44.3778 36.8217 45.2236 35.7783 45.2236H32.0001C30.9568 45.2236 30.111 44.3778 30.111 43.3344V30.1106H28.2219C27.1785 30.1106 26.3327 29.2648 26.3327 28.2215ZM31.9977 16.8873C30.7929 16.8873 29.8163 17.864 29.8163 19.0687C29.8163 20.2735 30.7929 21.2501 31.9977 21.2501H32.0413C33.246 21.2501 34.2227 20.2735 34.2227 19.0687C34.2227 17.864 33.246 16.8873 32.0413 16.8873H31.9977Z"
        fill="#2196F3"
      />
    </svg>
  ),
  loading: null
} as const;

function ToastDisplay(props: ToastProps) {
  const {
    variant = 'loading',
    message,
    title,
    visible,
    id,
    error,
    canDismiss = true,
    actions = [],
    icon
  } = props;

  const err = [];
  if (error.message) err.push(error.message);
  if (error.responseStatus)
    err.push(...((error.responseStatus.message as string) ?? '').split('\n'));

  return (
    <div
      className={`${bgMap[variant]} ${
        visible ? 'animate-enter' : 'animate-leave'
      } shadow-black-500/50 inline-flex max-h-36 w-96 select-none flex-col items-start justify-start rounded-md pt-1.5 shadow-md`}
    >
      <div className="flex min-w-36 flex-col items-start justify-start gap-1.5 self-stretch rounded-b-md bg-white ">
        <div className="inline-flex items-start justify-end gap-3 self-stretch p-3">
          {icon ?? iconMap[variant]}
          <div className="inline-flex shrink grow basis-0 flex-col items-start justify-start gap-4 py-1.5">
            <div className="text-zinc-900 flex max-h-5 w-full self-stretch text-base font-bold leading-normal ">
              <div className="grow">{title}</div>
              {canDismiss && (
                <FontAwesomeIcon
                  title={'Close'}
                  icon={faXmark as IconProp}
                  onClick={() => toast.remove(id)}
                  className={`h-6 w-6 cursor-pointer ${closeMap[variant]}`}
                />
              )}
            </div>
            {message && (
              <div className="self-stretch text-sm font-normal leading-tight">{message}</div>
            )}
            {err.length > 0 && (
              <div className="flex flex-col gap-1.5 text-xs text-gray-400">
                {err.map((e) => (
                  <div>{e}</div>
                ))}
              </div>
            )}
            {actions?.length > 0 && (
              <div className="flex gap-2 p-1.5">
                {actions.map((a, i) => (
                  <Button
                    onClick={() => {
                      a.onClick();
                      toast.remove(id);
                    }}
                    label={a.label}
                    key={a.label + i}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
